// The signed-in customer, for the /account pages.
//
// Tokens: the ID token lives in memory only; the refresh token is kept in
// sessionStorage so a reload (or a return from Stripe) in the same tab stays
// signed in, while closing the tab ends it. Nothing goes to localStorage: on a
// shared device the next person must not inherit the session. Storage access is
// wrapped in try/catch - private windows and blocked site data throw, and the
// page must still work (the customer just signs in again after a reload).

import { create } from 'zustand'
import {
  answerEmailCode,
  customerPoolConfig,
  emailOf,
  refreshTokens,
  revokeRefreshToken,
  startEmailSignIn,
  type CustomerTokens,
} from './cognito'

const REFRESH_KEY = 'helin.customer.refresh'

/** Refresh this long before the ID token actually expires. */
const EXPIRY_MARGIN_MS = 60_000

export type SessionStatus = 'restoring' | 'signedOut' | 'signedIn'

interface CustomerSessionState {
  status: SessionStatus
  email: string | null
  tokens: CustomerTokens | null
  /** Picks up a session left in this tab (reload, return from Stripe). Idempotent. */
  restore: () => Promise<void>
  /** Step 1: email a code. Returns the session to answer it with. */
  requestCode: (email: string) => Promise<string>
  /** Step 2: answer the code; signs the customer in. */
  verifyCode: (email: string, session: string, code: string) => Promise<void>
  /** A valid ID token, refreshed when (nearly) expired or when `force`d; null when signed out. */
  idToken: (force?: boolean) => Promise<string | null>
  signOut: () => Promise<void>
}

function readRefresh(): string | null {
  try {
    return sessionStorage.getItem(REFRESH_KEY)
  } catch {
    return null
  }
}

function writeRefresh(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(REFRESH_KEY, token)
    else sessionStorage.removeItem(REFRESH_KEY)
  } catch {
    // Storage unavailable: the session simply won't survive a reload.
  }
}

let refreshing: Promise<string | null> | null = null

export const useCustomerSession = create<CustomerSessionState>((set, get) => {
  const signedIn = (tokens: CustomerTokens) => {
    writeRefresh(tokens.refreshToken)
    set({ status: 'signedIn', tokens, email: emailOf(tokens.idToken) })
  }
  const signedOut = () => {
    writeRefresh(null)
    set({ status: 'signedOut', tokens: null, email: null })
  }

  /** One refresh at a time, however many requests hit an expired token together. */
  const refresh = (refreshToken: string): Promise<string | null> => {
    refreshing ??= refreshTokens(refreshToken)
      .then((tokens) => {
        signedIn(tokens)
        return tokens.idToken
      })
      .catch(() => {
        signedOut()
        return null
      })
      .finally(() => {
        refreshing = null
      })
    return refreshing
  }

  return {
    status: 'restoring',
    email: null,
    tokens: null,

    restore: async () => {
      if (get().status !== 'restoring') return
      const stored = customerPoolConfig() ? readRefresh() : null
      if (!stored) {
        signedOut()
        return
      }
      await refresh(stored)
    },

    requestCode: (email) => startEmailSignIn(email.trim().toLowerCase()),

    verifyCode: async (email, session, code) => {
      signedIn(await answerEmailCode(email.trim().toLowerCase(), session, code.trim()))
    },

    idToken: async (force = false) => {
      const { tokens } = get()
      if (!tokens) return null
      if (!force && tokens.expiresAt - EXPIRY_MARGIN_MS > Date.now()) return tokens.idToken
      return refresh(tokens.refreshToken)
    },

    signOut: async () => {
      const token = get().tokens?.refreshToken ?? readRefresh()
      signedOut()
      if (token) await revokeRefreshToken(token).catch(() => undefined)
    },
  }
})
